import { supabase } from "../../../supabaseClient.jsx";
import { normalizePaymentStatus } from "../paymentStatus.js";

const CHECKOUT_TIMEOUT_MS = 15_000;

/**
 * Classifica erros de rede/servidor em mensagens amigáveis ao usuário.
 * Retorna um Error com `code` anexado para o componente poder reagir.
 */
function classifyError(err, response) {
  // Timeout explícito (AbortController)
  if (err?.name === "AbortError") {
    const e = new Error(
      "A requisição demorou demais. Verifique sua conexão e tente novamente.",
    );
    e.code = "TIMEOUT";
    return e;
  }
  // Falha de rede (sem resposta do servidor)
  if (err instanceof TypeError && err.message.toLowerCase().includes("fetch")) {
    const e = new Error(
      "Sem conexão com o servidor de pagamentos. Verifique sua internet e tente novamente.",
    );
    e.code = "NETWORK";
    return e;
  }
  // Erro de autenticação
  if (response?.status === 401 || response?.status === 403) {
    const e = new Error("Sessão expirada. Faça login novamente para continuar.");
    e.code = "AUTH";
    return e;
  }
  // Erros genéricos de servidor
  if (response && response.status >= 500) {
    const e = new Error(
      "Nosso sistema de pagamentos está temporariamente indisponível. Tente em alguns minutos.",
    );
    e.code = "SERVER";
    return e;
  }
  return err;
}

export const mercadoPagoGateway = {
  provider: "mercadopago",

  async createCheckoutSession({ accessToken, userEmail, items }) {
    const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:4000";

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CHECKOUT_TIMEOUT_MS);

    let response;
    try {
      response = await fetch(
        `${backendUrl}/api/pagamentos/criar-preferencia-carrinho`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ userEmail, itens: items }),
          signal: controller.signal,
        },
      );
    } catch (fetchErr) {
      clearTimeout(timeoutId);
      throw classifyError(fetchErr, null);
    } finally {
      clearTimeout(timeoutId);
    }

    let data;
    try {
      data = await response.json();
    } catch {
      throw classifyError(null, response);
    }

    if (!response.ok || !data.init_point) {
      const serverMsg = data?.details || data?.error || null;
      // Recusa bancária / configuração MP
      if (serverMsg?.toLowerCase().includes("access token")) {
        const e = new Error(
          "Configuração de pagamento inválida. Contate o suporte.",
        );
        e.code = "CONFIG";
        throw e;
      }
      const e = new Error(serverMsg || "Falha ao gerar link de pagamento.");
      e.code = "CHECKOUT_ERROR";
      throw e;
    }

    return {
      transaction: null,
      redirectUrl: data.init_point,
    };
  },

  async listPayments({ userId }) {
    const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:4000";

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        return [];
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), CHECKOUT_TIMEOUT_MS);

      let response;
      try {
        response = await fetch(
          `${backendUrl}/api/pagamentos/listar?userId=${encodeURIComponent(userId)}`,
          {
            headers: { Authorization: `Bearer ${session.access_token}` },
            signal: controller.signal,
          },
        );
      } finally {
        clearTimeout(timeoutId);
      }

      if (!response.ok) return [];

      const data = await response.json();
      if (!Array.isArray(data)) return [];

      return data.map((row) => ({
        id: row.id,
        status: normalizePaymentStatus(row.status || row.status_pagamento),
        paymentMethod: row.metodo || "pix",
        total: Number(row.valor || 0),
        createdAt: row.data || row.created_at || new Date().toISOString(),
        items: [{ nome: row.nome_pacote || row.titulo || "Pagamento" }],
      }));
    } catch {
      return [];
    }
  },

  async updatePaymentStatus() {
    return null;
  },
};
